var __typeError = (msg) => {
  throw TypeError(msg);
};
var __accessCheck = (obj, member, msg) => member.has(obj) || __typeError("Cannot " + msg);
var __privateGet = (obj, member, getter) => (__accessCheck(obj, member, "read from private field"), getter ? getter.call(obj) : member.get(obj));
var __privateAdd = (obj, member, value) => member.has(obj) ? __typeError("Cannot add the same private member more than once") : member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
var __privateSet = (obj, member, value, setter) => (__accessCheck(obj, member, "write to private field"), setter ? setter.call(obj, value) : member.set(obj, value), value);
var __privateMethod = (obj, member, method) => (__accessCheck(obj, member, "access private method"), method);
import { animate, animateSequence, FocusTrap, Delegate } from "vendor";

function matchesMediaQuery(breakpointName) {
  if (!window.themeVariables.mediaQueries.hasOwnProperty(breakpointName)) {
    return false;
  }
  return window.matchMedia(window.themeVariables.mediaQueries[breakpointName]).matches;
}

// js/common/overlay/dialog-element.js
var lockLayerCount = 0;
var _isLocked, _delegate2, _focusLeaveDelegate, _abortController6, _focusTrap, _isTransitioningToState, _originalParentBeforeAppend, _DialogElement_instances, allowOutsideClick_fn, allowOutsideClickTouch_fn, allowOutsideClickMouse_fn, onActivatorClicked_fn, onActivatorPointerEnter_fn, updateSlotVisibility_fn;
var DialogElement = class extends HTMLElement {
  constructor() {
    super();
    __privateAdd(this, _DialogElement_instances);
    __privateAdd(this, _isLocked, false);
    __privateAdd(this, _delegate2, new Delegate(document.body));
    __privateAdd(this, _focusLeaveDelegate, new Delegate(document.body));
    __privateAdd(this, _abortController6);
    __privateAdd(this, _focusTrap);
    __privateAdd(this, _isTransitioningToState);
    __privateAdd(this, _originalParentBeforeAppend);
    this.setAttribute("role", "dialog");
    if (this.shadowDomTemplate) {
      this.attachShadow({ mode: "open" }).appendChild(this.shadowDomTemplate.content.cloneNode(true));
      this.shadowRoot.addEventListener("slotchange", (event) => __privateMethod(this, _DialogElement_instances, updateSlotVisibility_fn).call(this, event.target));
    }
    this.addEventListener("dialog:force-close", (event) => {
      this.hide();
      event.stopPropagation();
    });
  }
  static get observedAttributes() {
    return ["open"];
  }
  connectedCallback() {
    if (this.id) {
      __privateGet(this, _delegate2).off();
      __privateGet(this, _delegate2).on("click", `[aria-controls="${this.id}"]`, __privateMethod(this, _DialogElement_instances, onActivatorClicked_fn).bind(this));
      if (window.matchMedia("screen and (pointer: fine)").matches) {
        __privateGet(this, _delegate2).on("pointerenter", `[aria-controls="${this.id}"][data-open-on-hover]`, __privateMethod(this, _DialogElement_instances, onActivatorPointerEnter_fn).bind(this), true);
      }
    }
    __privateSet(this, _abortController6, new AbortController());
    if (this.shadowDomTemplate) {
      this.getShadowPartByName("overlay")?.addEventListener("click", this.hide.bind(this), { signal: this.abortController.signal });
      Array.from(this.shadowRoot.querySelectorAll("slot")).forEach((slot) => __privateMethod(this, _DialogElement_instances, updateSlotVisibility_fn).call(this, slot));
    }
    if (Shopify.designMode) {
      this._shopifySection = this._shopifySection || this.closest(".shopify-section");
      if (this._shopifySection) {
        if (this.hasAttribute("handle-editor-events")) {
          this._shopifySection.addEventListener("shopify:section:select", (event) => this.show({ animate: !event.detail.load }), { signal: this.abortController.signal });
          this._shopifySection.addEventListener("shopify:section:deselect", this.hide.bind(this), { signal: this.abortController.signal });
        }
        this._shopifySection.addEventListener("shopify:section:unload", () => this.remove(), { signal: this.abortController.signal });
      }
    }
  }
  disconnectedCallback() {
    __privateGet(this, _delegate2).off();
    this.abortController.abort();
    this.focusTrap?.deactivate({ onDeactivate: () => {
    } });
    if (__privateGet(this, _isLocked)) {
      __privateSet(this, _isLocked, false);
      document.documentElement.classList.toggle("lock", --lockLayerCount > 0);
    }
  }
  /**
   * Open the dialog element (the animation can be disabled by passing false as an argument). It optionally accepts two parameters:
   *
   * animate: if set to false, the dialog will open immediately without any animation
   * activator: the activator that has been used to show the dialog (if any)
   */
  async show({ animate: animate25 = true, activator, conditionToFulfill } = {}) {
    if (__privateGet(this, _isTransitioningToState) === "open") {
      return Promise.resolve();
    }
    __privateSet(this, _isTransitioningToState, "open");
    this.controls.forEach((activator2) => activator2.setAttribute("aria-expanded", "true"));
    this.setAttribute("open", "");
    if (conditionToFulfill) {
      await conditionToFulfill;
    }
    __privateSet(this, _originalParentBeforeAppend, null);
    this.style.removeProperty("display");
    this.style.setProperty("display", this.shadowRoot ? "contents" : getComputedStyle(this).display);
    this.dispatchEvent(new CustomEvent("dialog:before-show", { detail: { activator } }));
    if (this.shouldAppendToBody && this.parentElement !== document.body) {
      __privateSet(this, _originalParentBeforeAppend, this.parentElement);
      document.body.append(this);
    }
    const animationControls = this.createEnterAnimationControls();
    if (!animate25) {
      animationControls.complete();
    }
    animationControls.then(() => {
      this.dispatchEvent(new CustomEvent("dialog:after-show", { detail: { activator } }));
      __privateSet(this, _isTransitioningToState, null);
    });
    if (this.shouldTrapFocus) {
      this.focusTrap.activate({
        checkCanFocusTrap: () => animationControls
      });
    }
    if (this.shouldLock) {
      lockLayerCount += 1;
      __privateSet(this, _isLocked, true);
      document.documentElement.classList.add("lock");
    }
    return animationControls;
  }
  /**
   * Hide the dialog element
   */
  hide() {
    if (__privateGet(this, _isTransitioningToState) === "close" && !this.open) {
      return Promise.resolve();
    }
    __privateSet(this, _isTransitioningToState, "close");
    this.controls.forEach((activator) => activator.setAttribute("aria-expanded", "false"));
    this.removeAttribute("open");
    this.dispatchEvent(new CustomEvent("dialog:before-hide"));
    const hideTransitionPromise = this.createLeaveAnimationControls();
    hideTransitionPromise.then(() => {
      if (this.parentElement === document.body && __privateGet(this, _originalParentBeforeAppend)) {
        if (document.getElementById(this.id) !== this) {
          return this.remove();
        }
        __privateGet(this, _originalParentBeforeAppend).appendChild(this);
        __privateSet(this, _originalParentBeforeAppend, null);
      }
      this.style.setProperty("display", "none");
      this.dispatchEvent(new CustomEvent("dialog:after-hide"));
      __privateSet(this, _isTransitioningToState, null);
    });
    this.focusTrap?.deactivate({
      checkCanReturnFocus: () => hideTransitionPromise
    });
    if (this.shouldLock) {
      __privateSet(this, _isLocked, false);
      document.documentElement.classList.toggle("lock", --lockLayerCount > 0);
    }
    return hideTransitionPromise;
  }
  /**
   * Get the abort controller used to clean listeners. You can retrieve it in children classes to add your own listeners
   * that will be cleaned when the element is removed or re-rendered
   */
  get abortController() {
    return __privateGet(this, _abortController6);
  }
  /**
   * Get all the elements controlling this dialog (typically, button). An element controls this dialog if it has an
   * aria-controls attribute matching the ID of this dialog element
   */
  get controls() {
    return Array.from(this.getRootNode().querySelectorAll(`[aria-controls="${this.id}"]`));
  }
  /**
   * Returns if the dialog is open or closed
   */
  get open() {
    return this.hasAttribute("open");
  }
  /**
   * If true is returned, then FocusTrap will activate and manage all the focus management. This is required for good
   * accessibility (such as keyboard management) and should normally not be set to false in children classes unless
   * there is a very good reason to do so
   */
  get shouldTrapFocus() {
    return true;
  }
  /**
   * When the dialog focus is trapped, define if the page is lock (not scrollable). This is usually desirable on
   * full screen modals
   */
  get shouldLock() {
    return false;
  }
  /**
   * By default, when the focus is trapped on an element, a click outside the trapped element close it. Sometimes, it
   * may be desirable to turn off all interactions so that all clicks outside don't do anything
   */
  get clickOutsideDeactivates() {
    return true;
  }
  /**
   * Sometimes (especially for drawer) we need to ensure that an element is on top of everything else. To do that,
   * we need to move the element to the body. We are doing that on open, and then restore the initial position on
   * close
   */
  get shouldAppendToBody() {
    return this.hasAttribute("append-to-body") ? true : false;
  }
  /**
   * Decide which element to focus first when the dialog focus is trapped. By default, the first focusable element
   * will be focused, but this can be overridden by passing a selector in the "initial-focus" attribute
   */
  get initialFocus() {
    return this.hasAttribute("initial-focus") ? this.getAttribute("initial-focus") === "false" ? false : this.querySelector(this.getAttribute("initial-focus")) : this.hasAttribute("tabindex") ? this : this.querySelector('input:not([type="hidden"])') || false;
  }
  /**
   * If set to true, then focus trap will not automatically scroll to the first focused element, which can cause
   * annoying experience.
   */
  get preventScrollWhenTrapped() {
    return true;
  }
  /**
   * Allow custom elements to define their own, custom trap stack. If none is defined, the default one will be used
   */
  get trapStack() {
    return null;
  }
  /**
   * If set to true, dialog can be close by clicking "escape"
   */
  get escapeDeactivates() {
    return true;
  }
  /**
   * Get the focus trap element configured with all the other attributes
   */
  get focusTrap() {
    return __privateSet(this, _focusTrap, __privateGet(this, _focusTrap) || new FocusTrap.createFocusTrap(this, {
      onDeactivate: this.hide.bind(this),
      allowOutsideClick: this.clickOutsideDeactivates ? __privateMethod(this, _DialogElement_instances, allowOutsideClick_fn).bind(this) : false,
      initialFocus: matchesMediaQuery("supports-hover") ? this.initialFocus : false,
      fallbackFocus: this,
      preventScroll: this.preventScrollWhenTrapped,
      escapeDeactivates: this.escapeDeactivates,
      tabbableOptions: {
        getShadowRoot: true
      },
      trapStack: this.trapStack
    }));
  }
  /**
   * Get the ShadowDOM template (if any). If there is one defined, the dialog automatically constructs it with the
   * shadow DOM. It will first attempt to check the "template" attribute and, if none is found, will fallback with
   * the first template children tag
   */
  get shadowDomTemplate() {
    if (this.hasAttribute("template")) {
      return document.getElementById(this.getAttribute("template"));
    }
    return this.querySelector(":scope > template");
  }
  /**
   * For dialog that use Shadow DOM, this allows a quick retrieval of parts by name
   */
  getShadowPartByName(name) {
    return this.shadowRoot?.querySelector(`[part="${name}"]`);
  }
  /**
   * Callback called when attributes changes. To show/hide the dialog, you should use exclusively the "show" and "hide"
   * methods. However, the theme might insert dialog that we want to be pre-opened (for instance if it has an error). To
   * do that, we monitor the "open" attribute and open the dialog if it is set
   */
  attributeChangedCallback(name, oldValue, newValue) {
    switch (name) {
      case "open":
        if (oldValue === null && newValue === "") {
          this.show();
        } else if (oldValue !== null && newValue === null) {
          this.hide();
        }
        break;
    }
  }
  /**
   * Create the animation controls for the enter animation
   */
  createEnterAnimationControls() {
    return animate(this, {}, { duration: 0 });
  }
  /**
   * Create the animation controls for the leave animation
   */
  createLeaveAnimationControls() {
    return animate(this, {}, { duration: 0 });
  }
  /**
   * When "clickOutsideDeactivates" is true, this method is called on the final click destination. If this method
   * returns true, then the dialog closes (if false, the dialog remains in its current state). By default, this
   * will close the dialog if a click is done outside the dialog. However, this may be overridden in children classes
   * to provide custom behavior (for instance, to only allow some elements to close the dialog)
   */
  hideForOutsideClickTarget(target) {
    if (this.controls.includes(target) && target.hasAttribute("data-open-on-hover")) {
      return false;
    }
    return !this.contains(target);
  }
  /**
   * When "clickOutsideDeactivates" is set to true, this method allows to control which element, when clicked, allows
   * to pass-through and have its behavior being executed
   */
  allowOutsideClickForTarget(target) {
    if (this.controls.includes(target) && target.hasAttribute("data-open-on-hover")) {
      return true;
    }
    return false;
  }
};
_isLocked = new WeakMap();
_delegate2 = new WeakMap();
_focusLeaveDelegate = new WeakMap();
_abortController6 = new WeakMap();
_focusTrap = new WeakMap();
_isTransitioningToState = new WeakMap();
_originalParentBeforeAppend = new WeakMap();
_DialogElement_instances = new WeakSet();
/**
 * If "clickOutsideDeactivates" is true, then this listener will be called on every click outside the element. This
 * allows function separates touch and non-touch events
 */
allowOutsideClick_fn = function(event) {
  if ("TouchEvent" in window && event instanceof TouchEvent) {
    return __privateMethod(this, _DialogElement_instances, allowOutsideClickTouch_fn).call(this, event);
  } else {
    return __privateMethod(this, _DialogElement_instances, allowOutsideClickMouse_fn).call(this, event);
  }
};
/**
 * If "clickOutsideDeactivates" is true, this listener will be called on every touch click outside the trapped
 * element. By default, this will allow any click outside to cause the dialog to close
 */
allowOutsideClickTouch_fn = function(event) {
  event.target.addEventListener("touchend", (subEvent) => {
    const endTarget = document.elementFromPoint(subEvent.changedTouches.item(0).clientX, subEvent.changedTouches.item(0).clientY);
    if (this.hideForOutsideClickTarget(endTarget)) {
      this.hide();
    }
  }, { once: true, signal: this.abortController.signal });
  return this.allowOutsideClickForTarget(event.target);
};
/**
 * If "clickOutsideDeactivates" is true, this listener will be called on every mouse click outside the trapped
 * element. By default, this will allow any click outside to cause the dialog to close.
 */
allowOutsideClickMouse_fn = function(event) {
  if (event.type !== "click") {
    return false;
  }
  if (this.hideForOutsideClickTarget(event.target)) {
    this.hide();
  }
  if (this.allowOutsideClickForTarget(event.target)) {
    return true;
  }
  let target = event.target, closestControl = event.target.closest("[aria-controls]");
  if (closestControl && closestControl.getAttribute("aria-controls") === this.id) {
    target = closestControl;
  }
  return this.id !== target.getAttribute("aria-controls");
};
/**
 * This function is called whenever an activator (an element controlling this dialog) is called. This simply open
 * the dialog if closed, or close it if open
 */
onActivatorClicked_fn = function(event, activator) {
  if (this.open && this.controls.includes(event.target) && event.target.tagName === "A" && event.target.hasAttribute("data-open-on-hover")) {
    return;
  }
  if (this.open && this.id === "mega-menu-drawer" && activator?.getAttribute?.("data-open-panel") === "search" && this.classList.contains("menu-drawer--show-search")) {
    event?.preventDefault();
    return;
  }
  event?.preventDefault();
  this.open ? this.hide() : this.show({ activator });
};
/**
 * This function is called whenever an activator with the attribute "data-open-on-hover" is hovered.
 */
onActivatorPointerEnter_fn = function(event, activator) {
  if ((this.id === "mega-menu-drawer" || this.id === "secondary-nav-menu-drawer") && window.__themeHeaderHoverReady === false) {
    return;
  }
  if (window.__themeHeaderSuppressHoverActivators?.has(activator)) {
    window.__themeHeaderSuppressHoverActivators.delete(activator);
    return;
  }
  if ((this.id === "mega-menu-drawer" || this.id === "secondary-nav-menu-drawer") && this.open) {
    if (this.id === "mega-menu-drawer") {
      const openSearch = activator?.getAttribute?.("data-open-panel") === "search";
      if (openSearch) {
        this.classList.add("menu-drawer--show-search");
        this.controls.forEach((navActivator) => {
          if (navActivator?.hasAttribute?.("data-link-position")) {
            navActivator.setAttribute("aria-current", "false");
          }
        });
        this.querySelectorAll('[aria-current][data-link-position]').forEach((navActivator) => navActivator.setAttribute("aria-current", "false"));
        this.querySelectorAll(".menu-drawer__mega-menu-level").forEach((level) => level.classList.remove("is-visible"));
        return;
      }
    }
    const position = activator?.getAttribute?.("data-link-position");
    if (position) {
      const levelWithLinks = this.querySelector(`.menu-drawer__mega-menu-level[data-level-position="${position}"][data-mega-has-links]`);
      const levelToShow = levelWithLinks;
      if (levelToShow) {
        this.classList.remove("menu-drawer--show-search");
        this.controls.forEach((navActivator) => {
          if (navActivator?.hasAttribute?.("data-link-position")) {
            navActivator.setAttribute("aria-current", "false");
          }
        });
        activator.setAttribute("aria-current", "true");
        this.querySelectorAll(".menu-drawer__mega-menu-level").forEach((level) => level.classList.remove("is-visible"));
        levelToShow.classList.add("is-visible");
        requestAnimationFrame(() => syncMegaMenuDrawerVisibleLevel(this));
        return;
      }
      this.controls.forEach((navActivator) => {
        if (navActivator?.hasAttribute?.("data-link-position")) {
          navActivator.setAttribute("aria-current", "false");
        }
      });
      this.querySelectorAll(".menu-drawer__mega-menu-level").forEach((level) => level.classList.remove("is-visible"));
      this.hide();
      return;
    }
  }
  if (!this.open) {
    const blurActivator = () => {
      if (document.activeElement === activator) {
        activator.blur();
      }
    };
    this.addEventListener("dialog:after-show", blurActivator, { once: true });
    const isReopeningDuringClose = __privateGet(this, _isTransitioningToState) === "close";
    this.show({ activator, animate: !isReopeningDuringClose });
    __privateGet(this, _focusLeaveDelegate).off().on("pointerenter", (event) => {
      const targetContainsDialog = event.target.contains(this);
      const dialogContainsTarget = this.contains(event.target);
      const targetControlsDialog = event.target.getAttribute("aria-controls") === this.id;
      const closestControlsDialog = event.target.closest("[aria-controls]")?.getAttribute("aria-controls") === this.id;
      const withinControlledDialog = Boolean(event.target?.closest?.(`[id="${this.id}"]`));
      const withinHeaderWhileMegaDrawer =
        (this.id === "mega-menu-drawer" || this.id === "secondary-nav-menu-drawer") &&
        Boolean(event.target.closest?.(".shopify-section--header"));
      const shouldKeepOpen =
        targetContainsDialog ||
        dialogContainsTarget ||
        targetControlsDialog ||
        closestControlsDialog ||
        withinControlledDialog ||
        withinHeaderWhileMegaDrawer;
      if (shouldKeepOpen) {
        return;
      }
      __privateGet(this, _focusLeaveDelegate).off();
      this.hide();
    }, true);
  }
};
/**
 * Hide the slots that do not have any children
 */
updateSlotVisibility_fn = function(slot) {
  if (!["header", "footer"].includes(slot.name)) {
    return;
  }
  slot.parentElement.hidden = slot.assignedElements({ flatten: true }).length === 0;
};
var DialogCloseButton = class extends HTMLElement {
  constructor() {
    super();
    this.addEventListener("click", () => this.dispatchEvent(new CustomEvent("dialog:force-close", { bubbles: true, cancelable: true, composed: true })));
  }
};
if (!window.customElements.get("dialog-element")) {
  window.customElements.define("dialog-element", DialogElement);
}
if (!window.customElements.get("dialog-close-button")) {
  window.customElements.define("dialog-close-button", DialogCloseButton);
}

var _HeaderMegaMenuDrawer_instances, onBeforeShow_fn4, onAfterHide_fn4;
var syncMegaMenuDrawerVisibleLevel = (drawer) => {
  const visibleLevel = drawer.querySelector(".menu-drawer__mega-menu-level.is-visible");
  const nav = visibleLevel?.querySelector(".mega-menu__navigation");
  const firstColumn = nav?.querySelector(".mega-menu__column");
  const navColumnGapValue = nav ? getComputedStyle(nav).columnGap : null;
  const menuColumnWidthValue = firstColumn?.getBoundingClientRect().width ?? null;
  const menuColumnCount = nav?.querySelectorAll(":scope > .mega-menu__column").length ?? 0;
  const totalColumnCount = menuColumnCount;
  if (visibleLevel && menuColumnWidthValue) {
    // Prefer CSS calc on the level for promo sizing; only keep a measured fallback when column count is unknown.
    if (!(totalColumnCount > 0)) {
      visibleLevel.style.setProperty("--mega-menu-column-width", `${menuColumnWidthValue}px`);
    } else {
      visibleLevel.style.removeProperty("--mega-menu-column-width");
    }
  }
  if (visibleLevel && navColumnGapValue) {
    visibleLevel.style.setProperty("--mega-menu-column-gap-live", navColumnGapValue);
  }
  if (menuColumnWidthValue && totalColumnCount > 0) {
    drawer.style.setProperty("--mega-menu-column-width-live", `${menuColumnWidthValue}px`);
    drawer.style.setProperty("--mega-menu-total-columns", `${totalColumnCount}`);
  } else {
    drawer.style.removeProperty("--mega-menu-column-width-live");
    drawer.style.removeProperty("--mega-menu-total-columns");
  }
  drawer.classList.toggle("menu-drawer--single-column", totalColumnCount <= 1);
  if (navColumnGapValue) {
    drawer.style.setProperty("--mega-menu-column-gap-live", navColumnGapValue);
  } else {
    drawer.style.removeProperty("--mega-menu-column-gap-live");
  }
  const header = document.querySelector(".shopify-section--header .header");
  const headerPrimaryNav = document.querySelector(".shopify-section--header .header__primary-nav");
  const headerSecondaryNav = document.querySelector(".shopify-section--header .header__secondary-nav");
  const contentPart = drawer.getShadowPartByName?.("content");
  if (header && contentPart) {
    const anchorForOffset = drawer.id === "secondary-nav-menu-drawer" ? headerSecondaryNav || headerPrimaryNav || header : headerPrimaryNav || header;
    const anchorRect = anchorForOffset.getBoundingClientRect();
    const contentRect = contentPart.getBoundingClientRect();
    const megaContentTopOffset = Math.max(0, anchorRect.bottom - contentRect.top);
    drawer.style.setProperty("--mega-menu-content-top-offset", `${megaContentTopOffset}px`);
    const headerComputedStyle = getComputedStyle(header);
    const headerPaddingInline = headerComputedStyle.getPropertyValue("--header-padding-inline")?.trim();
    if (headerPaddingInline) {
      drawer.style.setProperty("--header-padding-inline", headerPaddingInline);
    }
  }
};
var _HeaderSecondaryMenuDrawer_instances, onBeforeShowSecondary_fn, onAfterHideSecondary_fn;
var HeaderSecondaryMenuDrawer = class extends DialogElement {
  constructor() {
    super();
    __privateAdd(this, _HeaderSecondaryMenuDrawer_instances);
    this.addEventListener("dialog:before-show", __privateMethod(this, _HeaderSecondaryMenuDrawer_instances, onBeforeShowSecondary_fn).bind(this));
    this.addEventListener("dialog:after-hide", __privateMethod(this, _HeaderSecondaryMenuDrawer_instances, onAfterHideSecondary_fn).bind(this));
  }
  connectedCallback() {
    super.connectedCallback();
    const mq = window.themeVariables?.mediaQueries?.["md-max"];
    if (mq) {
      window.matchMedia(mq).addEventListener(
        "change",
        (event) => {
          if (event.matches && this.open) {
            this.hide();
          }
        },
        { signal: this.abortController.signal }
      );
    }
    document.addEventListener(
      "pointermove",
      (event) => {
        if (!this.open) {
          return;
        }
        const isMobile = mq ? window.matchMedia(mq).matches : false;
        if (isMobile) {
          return;
        }
        const contentPart = this.getShadowPartByName("content");
        if (!contentPart) {
          return;
        }
        const contentRect = contentPart.getBoundingClientRect();
        const isWithinContent = event.clientX >= contentRect.left && event.clientX <= contentRect.right && event.clientY >= contentRect.top && event.clientY <= contentRect.bottom;
        const isWithinActivator = this.controls.some((control) => {
          const rect = control.getBoundingClientRect();
          return event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
        });
        const headerSection = document.querySelector(".shopify-section--header");
        const headerRect = headerSection?.getBoundingClientRect();
        const isWithinHeader = headerRect ? event.clientX >= headerRect.left && event.clientX <= headerRect.right && event.clientY >= headerRect.top && event.clientY <= headerRect.bottom : false;
        const shouldClose = !isWithinContent && !isWithinActivator && !isWithinHeader;
        const visibleMegaLevel = this.querySelector(".menu-drawer__mega-menu-level.is-visible");
        const controlCurrentStatesWhileOpen = this.controls.filter((control) => control.hasAttribute?.("data-link-position")).map((control) => ({
          position: control.getAttribute("data-link-position"),
          ariaCurrent: control.getAttribute("aria-current")
        }));
        const activeHeaderControlCount = controlCurrentStatesWhileOpen.filter((item) => item.ariaCurrent === "true").length;
        if (visibleMegaLevel && activeHeaderControlCount === 0) {
          const visiblePosition = visibleMegaLevel.getAttribute("data-level-position");
          if (visiblePosition) {
            this.controls.forEach((control) => {
              if (control?.hasAttribute?.("data-link-position")) {
                control.setAttribute("aria-current", control.getAttribute("data-link-position") === visiblePosition ? "true" : "false");
              }
            });
          }
        }
        if (!shouldClose) {
          return;
        }
        this.hide();
      },
      { signal: this.abortController.signal }
    );
    window.addEventListener(
      "resize",
      () => {
        if (this.open) {
          requestAnimationFrame(() => syncMegaMenuDrawerVisibleLevel(this));
        }
      },
      { signal: this.abortController.signal }
    );
    const resizeObserver = new ResizeObserver(() => {
      if (this.open) {
        requestAnimationFrame(() => syncMegaMenuDrawerVisibleLevel(this));
      }
    });
    resizeObserver.observe(this);
    this.abortController.signal.addEventListener("abort", () => resizeObserver.disconnect(), { once: true });
  }
  get initialFocus() {
    return this.querySelector(".menu-drawer__close-button") || false;
  }
  get shouldLock() {
    return true;
  }
  createEnterAnimationControls() {
    const timelineSteps = [
      [this.getShadowPartByName("overlay"), { opacity: [0, 1] }, { duration: 0.2 }]
    ];
    if (window.matchMedia("(prefers-reduced-motion: no-preference)").matches) {
      timelineSteps.push(
        [this.getShadowPartByName("content"), { transform: ["translateX(calc(var(--transform-logical-flip) * 100%))", "translateX(0)"] }, { duration: 0.35, at: "<", ease: [0.2, 0.4, 0.2, 1] }]
      );
    } else {
      timelineSteps.push(
        [this.getShadowPartByName("content"), { opacity: [0, 1] }, { duration: 0.2, at: "<" }]
      );
    }
    return animateSequence(timelineSteps);
  }
  createLeaveAnimationControls() {
    const timelineSteps = [
      [this.getShadowPartByName("overlay"), { opacity: [1, 0] }, { duration: 0.25 }]
    ];
    if (window.matchMedia("(prefers-reduced-motion: no-preference)").matches) {
      timelineSteps.push(
        [this.getShadowPartByName("content"), { transform: ["translateX(0)", "translateX(calc(var(--transform-logical-flip) * 100%))"] }, { duration: 0.25, at: "<", ease: [0.645, 0.045, 0.355, 1] }]
      );
    } else {
      timelineSteps.push(
        [this.getShadowPartByName("content"), { opacity: [1, 0] }, { duration: 0.2, at: "<" }]
      );
    }
    return animateSequence(timelineSteps);
  }
};
_HeaderSecondaryMenuDrawer_instances = new WeakSet();
onBeforeShowSecondary_fn = function(event) {
  document.documentElement.dispatchEvent(new CustomEvent("header:restore-after-global-overlay", { bubbles: true }));
  requestAnimationFrame(() => {
    const headerSection = document.querySelector(".shopify-section--header");
    const headerElement = headerSection?.querySelector(".header");
    const headerSectionRect = headerSection?.getBoundingClientRect?.();
    const headerElementRect = headerElement?.getBoundingClientRect?.();
    const stickyAreaHeight = parseFloat(getComputedStyle(document.documentElement).getPropertyValue("--sticky-area-height")) || 0;
    const measuredOffsetTop = Math.max(
      0,
      Math.round(headerSectionRect?.bottom ?? 0),
      Math.round(headerElementRect?.bottom ?? 0),
      Math.round(stickyAreaHeight)
    );
    const existingOffsetTop = parseFloat(this.style.getPropertyValue("--menu-offset-top")) || 0;
    const safeMenuOffsetTop = measuredOffsetTop > 0 ? measuredOffsetTop : existingOffsetTop;
    this.style.setProperty("--menu-offset-top", `${safeMenuOffsetTop}px`);
    const overlayPartForOffset = this.getShadowPartByName?.("overlay");
    if (overlayPartForOffset) {
      overlayPartForOffset.style.insetBlockStart = `${safeMenuOffsetTop}px`;
    }
  });
  this.controls.forEach((control) => {
    if (control?.hasAttribute?.("data-link-position")) {
      control.setAttribute("aria-current", "false");
    }
  });
  this.querySelectorAll(".menu-drawer__mega-menu-level").forEach((level) => level.classList.remove("is-visible"));
  this.querySelectorAll("[aria-current][data-link-position]").forEach((activator2) => activator2.setAttribute("aria-current", "false"));
  const activator = event.detail?.activator;
  const position = activator?.getAttribute("data-link-position");
  const levelToShow = position ? this.querySelector(`.menu-drawer__mega-menu-level[data-level-position="${position}"][data-mega-has-links]`) : null;
  if (levelToShow) {
    levelToShow.classList.add("is-visible");
    activator?.setAttribute?.("aria-current", "true");
    this.controls.find((control) => control.getAttribute?.("data-link-position") === position)?.setAttribute("aria-current", "true");
  }
  requestAnimationFrame(() => syncMegaMenuDrawerVisibleLevel(this));
};
onAfterHideSecondary_fn = function() {
  this.querySelectorAll(".menu-drawer__mega-menu-level").forEach((level) => level.classList.remove("is-visible"));
  this.controls.forEach((activator) => {
    if (activator?.hasAttribute?.("data-link-position")) {
      activator.setAttribute("aria-current", "false");
    }
  });
  document.documentElement.dispatchEvent(new CustomEvent("header:restore-after-global-overlay", { bubbles: true }));
};
var HeaderMegaMenuDrawer = class extends DialogElement {
  constructor() {
    super();
    __privateAdd(this, _HeaderMegaMenuDrawer_instances);
    this.debugPinStorageKey = "theme:debug:pin-mega-menu";
    this.addEventListener("dialog:before-show", __privateMethod(this, _HeaderMegaMenuDrawer_instances, onBeforeShow_fn4).bind(this));
    this.addEventListener("dialog:after-hide", __privateMethod(this, _HeaderMegaMenuDrawer_instances, onAfterHide_fn4).bind(this));
    if (!window.themeDebugMegaMenuPin) {
      window.themeDebugMegaMenuPin = {
        enable: () => {
          localStorage.setItem(this.debugPinStorageKey, "true");
          document.querySelectorAll("header-mega-menu-drawer").forEach((drawer) => drawer.classList.add("menu-drawer--debug-pinned"));
          return true;
        },
        disable: () => {
          localStorage.removeItem(this.debugPinStorageKey);
          document.querySelectorAll("header-mega-menu-drawer").forEach((drawer) => drawer.classList.remove("menu-drawer--debug-pinned"));
          return false;
        },
        toggle: () => {
          const isPinned = localStorage.getItem(this.debugPinStorageKey) === "true";
          return isPinned ? window.themeDebugMegaMenuPin.disable() : window.themeDebugMegaMenuPin.enable();
        },
        status: () => localStorage.getItem(this.debugPinStorageKey) === "true"
      };
    }
  }
  connectedCallback() {
    super.connectedCallback();
    const mq = window.themeVariables?.mediaQueries?.["md-max"];
    if (mq) {
      window.matchMedia(mq).addEventListener(
        "change",
        (event) => {
          if (event.matches && this.open) {
            this.hide();
          }
        },
        { signal: this.abortController.signal }
      );
    }
    document.addEventListener(
      "pointermove",
      (event) => {
        if (!this.open) {
          return;
        }
        const isMobile = mq ? window.matchMedia(mq).matches : false;
        if (isMobile) {
          return;
        }
        const contentPart = this.getShadowPartByName("content");
        if (!contentPart) {
          return;
        }
        const contentRect = contentPart.getBoundingClientRect();
        const isWithinContent = event.clientX >= contentRect.left && event.clientX <= contentRect.right && event.clientY >= contentRect.top && event.clientY <= contentRect.bottom;
        const isWithinActivator = this.controls.some((control) => {
          const rect = control.getBoundingClientRect();
          return event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
        });
        const headerSection = document.querySelector(".shopify-section--header");
        const headerRect = headerSection?.getBoundingClientRect();
        const isWithinHeader = headerRect ? event.clientX >= headerRect.left && event.clientX <= headerRect.right && event.clientY >= headerRect.top && event.clientY <= headerRect.bottom : false;
        const shouldClose = !isWithinContent && !isWithinActivator && !isWithinHeader;
        const visibleMegaLevel = this.querySelector(".menu-drawer__mega-menu-level.is-visible");
        const controlCurrentStatesWhileOpen = this.controls.filter((control) => control.hasAttribute?.("data-link-position")).map((control) => ({
          position: control.getAttribute("data-link-position"),
          ariaCurrent: control.getAttribute("aria-current")
        }));
        const activeHeaderControlCount = controlCurrentStatesWhileOpen.filter((item) => item.ariaCurrent === "true").length;
        if (visibleMegaLevel && activeHeaderControlCount === 0) {
          const visiblePosition = visibleMegaLevel.getAttribute("data-level-position");
          if (visiblePosition) {
            this.controls.forEach((control) => {
              if (control?.hasAttribute?.("data-link-position")) {
                control.setAttribute("aria-current", control.getAttribute("data-link-position") === visiblePosition ? "true" : "false");
              }
            });
          }
        }
        if (!shouldClose) {
          return;
        }
        if (localStorage.getItem(this.debugPinStorageKey) === "true") {
          return;
        }
        this.hide();
      },
      { signal: this.abortController.signal }
    );
    window.addEventListener(
      "resize",
      () => {
        if (this.open) {
          requestAnimationFrame(() => syncMegaMenuDrawerVisibleLevel(this));
        }
      },
      { signal: this.abortController.signal }
    );
    const resizeObserver = new ResizeObserver(() => {
      if (this.open) {
        requestAnimationFrame(() => syncMegaMenuDrawerVisibleLevel(this));
      }
    });
    resizeObserver.observe(this);
    this.abortController.signal.addEventListener("abort", () => resizeObserver.disconnect(), { once: true });
    this.classList.toggle("menu-drawer--debug-pinned", localStorage.getItem(this.debugPinStorageKey) === "true");
  }
  get initialFocus() {
    return this.querySelector(".menu-drawer__close-button") || false;
  }
  get shouldLock() {
    return true;
  }
  createEnterAnimationControls() {
    const timelineSteps = [
      [this.getShadowPartByName("overlay"), { opacity: [0, 1] }, { duration: 0.2 }]
    ];
    if (window.matchMedia("(prefers-reduced-motion: no-preference)").matches) {
      timelineSteps.push(
        [this.getShadowPartByName("content"), { transform: ["translateX(calc(var(--transform-logical-flip) * -100%))", "translateX(0)"] }, { duration: 0.35, at: "<", ease: [0.2, 0.4, 0.2, 1] }]
      );
    } else {
      timelineSteps.push(
        [this.getShadowPartByName("content"), { opacity: [0, 1] }, { duration: 0.2, at: "<" }]
      );
    }
    return animateSequence(timelineSteps);
  }
  createLeaveAnimationControls() {
    const timelineSteps = [
      [this.getShadowPartByName("overlay"), { opacity: [1, 0] }, { duration: 0.25 }]
    ];
    if (window.matchMedia("(prefers-reduced-motion: no-preference)").matches) {
      timelineSteps.push(
        [this.getShadowPartByName("content"), { transform: ["translateX(0)", "translateX(calc(var(--transform-logical-flip) * -100%))"] }, { duration: 0.25, at: "<", ease: [0.645, 0.045, 0.355, 1] }]
      );
    } else {
      timelineSteps.push(
        [this.getShadowPartByName("content"), { opacity: [1, 0] }, { duration: 0.2, at: "<" }]
      );
    }
    return animateSequence(timelineSteps);
  }
};
_HeaderMegaMenuDrawer_instances = new WeakSet();
onBeforeShow_fn4 = function(event) {
  document.documentElement.dispatchEvent(new CustomEvent("header:restore-after-global-overlay", { bubbles: true }));
  requestAnimationFrame(() => {
    const headerSection = document.querySelector(".shopify-section--header");
    const headerElement = headerSection?.querySelector(".header");
    const headerSectionRect = headerSection?.getBoundingClientRect?.();
    const headerElementRect = headerElement?.getBoundingClientRect?.();
    const stickyAreaHeight = parseFloat(getComputedStyle(document.documentElement).getPropertyValue("--sticky-area-height")) || 0;
    const measuredOffsetTop = Math.max(
      0,
      Math.round(headerSectionRect?.bottom ?? 0),
      Math.round(headerElementRect?.bottom ?? 0),
      Math.round(stickyAreaHeight)
    );
    const existingOffsetTop = parseFloat(this.style.getPropertyValue("--menu-offset-top")) || 0;
    const safeMenuOffsetTop = measuredOffsetTop > 0 ? measuredOffsetTop : existingOffsetTop;
    this.style.setProperty("--menu-offset-top", `${safeMenuOffsetTop}px`);
    const overlayPartForOffset = this.getShadowPartByName?.("overlay");
    if (overlayPartForOffset) {
      overlayPartForOffset.style.insetBlockStart = `${safeMenuOffsetTop}px`;
    }
    const overlayPart = this.getShadowPartByName?.("overlay");
    const headerRect = headerSection?.getBoundingClientRect?.();
    const overlayRect = overlayPart?.getBoundingClientRect?.();
  });
  this.controls.forEach((control) => {
    if (control?.hasAttribute?.("data-link-position")) {
      control.setAttribute("aria-current", "false");
    }
  });
  this.querySelectorAll(".menu-drawer__mega-menu-level").forEach((level) => level.classList.remove("is-visible"));
  this.querySelectorAll("[aria-current][data-link-position]").forEach((activator2) => activator2.setAttribute("aria-current", "false"));
  const activator = event.detail?.activator;
  const openSearch = activator?.getAttribute?.("data-open-panel") === "search";
  if (openSearch) {
    this.classList.add("menu-drawer--show-search");
  } else {
    this.classList.remove("menu-drawer--show-search");
    const position = activator?.getAttribute("data-link-position");
    const levelToShow = position ? this.querySelector(`.menu-drawer__mega-menu-level[data-level-position="${position}"][data-mega-has-links]`) : null;
    if (levelToShow) {
      levelToShow.classList.add("is-visible");
      this.querySelector(`[aria-current][data-link-position="${position}"]`)?.setAttribute("aria-current", "true");
      this.controls.find((control) => control.getAttribute?.("data-link-position") === position)?.setAttribute("aria-current", "true");
    }
  }
  requestAnimationFrame(() => syncMegaMenuDrawerVisibleLevel(this));
};
onAfterHide_fn4 = function() {
  this.classList.remove("menu-drawer--show-search");
  this.querySelectorAll(".menu-drawer__mega-menu-level").forEach((level) => level.classList.remove("is-visible"));
  this.controls.forEach((activator) => {
    if (activator?.hasAttribute?.("data-link-position")) {
      activator.setAttribute("aria-current", "false");
    }
  });
  document.documentElement.dispatchEvent(new CustomEvent("header:restore-after-global-overlay", { bubbles: true }));
};
if (!window.customElements.get("header-secondary-menu-drawer")) {
  window.customElements.define("header-secondary-menu-drawer", HeaderSecondaryMenuDrawer);
}
if (!window.customElements.get("header-mega-menu-drawer")) {
  window.customElements.define("header-mega-menu-drawer", HeaderMegaMenuDrawer);
}

if (!window.__themeHeaderHoverReady && window.matchMedia("(pointer: fine)").matches) {
  window.__themeHeaderSuppressHoverActivators = new WeakSet();
  document.querySelectorAll('[aria-controls="mega-menu-drawer"][data-open-on-hover], [aria-controls="secondary-nav-menu-drawer"][data-open-on-hover]').forEach((activator) => {
    if (activator.matches(":hover")) {
      window.__themeHeaderSuppressHoverActivators.add(activator);
    }
  });
  window.__themeHeaderHoverReady = true;
}
window.__themeHeaderMarkReady?.("headerMenu");

export { DialogElement, DialogCloseButton, lockLayerCount };
